/**
 * Bootstrap Theme Toggle Button
 * Simple toggle for switching between light and dark Bootstrap themes
 */

import { Fragment } from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';

interface BootstrapThemeToggleProps {
  className?: string;
  size?: 'sm' | 'lg';
  variant?: 'outline' | 'solid';
  showLabel?: boolean;
}

export function BootstrapThemeToggle({ 
  className = '', 
  size,
  variant = 'outline',
  showLabel = false 
}: BootstrapThemeToggleProps) {
  const { theme, effectiveTheme, setTheme, toggleTheme } = useBootstrapTheme();

  const sizeClass = size ? `btn-${size}` : '';
  const variantClass = variant === 'solid' ? 'btn-secondary' : 'btn-outline-secondary';

  const handleClick = () => {
    if (theme === 'auto') {
      // If in auto mode, switch to opposite of current effective theme
      setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
    } else {
      // Normal toggle
      toggleTheme();
    }
  };

  const getIcon = () => {
    const sz = size === 'sm' ? 14 : 16;
    if (theme === 'auto') return <Monitor size={sz} strokeWidth={2} />;
    return effectiveTheme === 'dark' ? <Moon size={sz} strokeWidth={2} /> : <Sun size={sz} strokeWidth={2} />;
  };

  const getLabel = () => {
    if (theme === 'auto') {
      return 'Auto';
    }
    return effectiveTheme === 'dark' ? 'Dark' : 'Light';
  };

  const getTitle = () => {
    if (theme === 'auto') {
      return `Auto theme (currently ${effectiveTheme}). Click to switch to ${effectiveTheme === 'dark' ? 'light' : 'dark'} theme.`;
    }
    return `Switch to ${effectiveTheme === 'dark' ? 'light' : 'dark'} theme`;
  };

  return (
    <button
      type="button"
      className={`btn ${variantClass} ${sizeClass} d-flex align-items-center gap-2 ${className}`}
      onClick={handleClick}
      title={getTitle()}
      aria-label={getTitle()}
    >
      {getIcon()}
      {showLabel && (
        <span className="d-none d-sm-inline">
          {getLabel()}
        </span>
      )}
    </button>
  );
}

// Compact version for toolbars
export function CompactThemeToggle({ className = '' }: { className?: string }) {
  return (
    <BootstrapThemeToggle
      className={`${className}`}
      size="sm"
      variant="outline"
      showLabel={false}
    />
  );
}

// Full version with label for settings
export function FullThemeToggle({ className = '' }: { className?: string }) {
  return (
    <BootstrapThemeToggle
      className={`${className}`}
      variant="outline"
      showLabel={true}
    />
  );
}

// Theme segmented control for settings
export function ThemeSegmentedControl({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useBootstrapTheme();
  
  return (
    <div className={`btn-group ${className}`} role="group" aria-label="Theme selection">
      {(['light', 'dark', 'auto'] as const).map((themeOption) => (
        <Fragment key={themeOption}>
          <input
            type="radio"
            className="btn-check"
            name="theme-options"
            id={`theme-${themeOption}`}
            checked={theme === themeOption}
            onChange={() => setTheme(themeOption)}
          />
          <label
            className="btn btn-outline-primary d-flex align-items-center gap-1"
            htmlFor={`theme-${themeOption}`}
          >
            {themeOption === 'light' ? <Sun size={14} strokeWidth={2} /> : themeOption === 'dark' ? <Moon size={14} strokeWidth={2} /> : <Monitor size={14} strokeWidth={2} />}
            {themeOption.charAt(0).toUpperCase() + themeOption.slice(1)}
          </label>
        </Fragment>
      ))}
    </div>
  );
}

// Theme status indicator (read-only)
export function ThemeIndicator({ className = '' }: { className?: string }) {
  const { theme, effectiveTheme } = useBootstrapTheme();
  
  return (
    <span className={`badge bg-secondary ${className}`} title={`Theme: ${theme} (${effectiveTheme})`}>
      {theme === 'auto' ? <Monitor size={12} strokeWidth={2} className="me-1" /> : effectiveTheme === 'dark' ? <Moon size={12} strokeWidth={2} className="me-1" /> : <Sun size={12} strokeWidth={2} className="me-1" />}
      <span className="small">{theme}</span>
    </span>
  );
}